// ReportContext — global state for the "Add to PDF report" drawer.
//
// Anywhere in the app, a button can call `useReport().addChart(...)` or
// `addAnswer(...)` to queue an insight into the drawer. The drawer (mounted
// once in App.tsx) reads the same context and renders the queue, lets the
// user edit titles / reorder / remove items, and dispatches a Generate
// click that POSTs everything to /reports/generate.
//
// Items live in-memory only — closing the tab discards the queue. The
// queue persists across chat turns and across the Search/Dashboards nav.

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ChartSpec } from "../charts/types";

export interface ReportChartItem {
  id: string;
  kind: "chart";
  title: string;
  userQuery: string;
  datasourceId?: string;
  // Captured at "add" time so the server can embed the rendered chart
  // verbatim. The user's currently-selected view (chart vs. table) is
  // what ends up in the PDF — we don't re-render server-side.
  imagePng: string;
  // Top-N rows of the underlying result so the report-author LLM has
  // real numbers to cite in the executive summary and per-item insight.
  // Without this the model only sees title + question and writes
  // generic prose. Capped to keep the prompt small.
  rows: ReadonlyArray<ReadonlyArray<string | number | boolean | null>>;
  columns: ReadonlyArray<string>;
  // Stash the spec for fallback/debug. Server reads `imagePng`.
  chartSpec: ChartSpec;
}

export interface ReportAnswerItem {
  id: string;
  kind: "answer";
  title: string;
  userQuery: string;
  text: string;
  insights: string[];
}

export type ReportItem = ReportChartItem | ReportAnswerItem;

interface ReportContextValue {
  items: ReportItem[];
  addChart: (item: Omit<ReportChartItem, "id" | "kind">) => void;
  addAnswer: (item: Omit<ReportAnswerItem, "id" | "kind">) => void;
  remove: (id: string) => void;
  move: (id: string, dir: -1 | 1) => void;
  setTitle: (id: string, title: string) => void;
  clear: () => void;
  /** Convenience: have we already queued this chart? Keyed by sql+title to
   *  avoid duplicate adds when the user clicks the button twice. */
  hasChartLike: (sql: string | undefined, title: string) => boolean;
}

const Ctx = createContext<ReportContextValue | null>(null);

function newId(): string {
  // Short opaque id; the server doesn't care about format. `crypto.randomUUID`
  // is widely available; fall back to a timestamp+rand for ancient browsers.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function ReportProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ReportItem[]>([]);

  const addChart = useCallback((data: Omit<ReportChartItem, "id" | "kind">) => {
    setItems((prev) => [...prev, { id: newId(), kind: "chart", ...data }]);
  }, []);

  const addAnswer = useCallback((data: Omit<ReportAnswerItem, "id" | "kind">) => {
    setItems((prev) => [...prev, { id: newId(), kind: "answer", ...data }]);
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const move = useCallback((id: string, dir: -1 | 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.id === id);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const copy = prev.slice();
      const [picked] = copy.splice(idx, 1);
      copy.splice(next, 0, picked);
      return copy;
    });
  }, []);

  const setTitle = useCallback((id: string, title: string) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, title } : it)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const hasChartLike = useCallback(
    (sql: string | undefined, title: string) => {
      const target = (sql || "").trim();
      return items.some(
        (it) =>
          it.kind === "chart" &&
          ((target && (it.chartSpec.sql ?? "").trim() === target) || it.title === title),
      );
    },
    [items],
  );

  const value = useMemo<ReportContextValue>(
    () => ({ items, addChart, addAnswer, remove, move, setTitle, clear, hasChartLike }),
    [items, addChart, addAnswer, remove, move, setTitle, clear, hasChartLike],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useReport(): ReportContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useReport must be used inside <ReportProvider>");
  return v;
}
